import React, { useState } from "react";
import * as XLSX from "xlsx";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./shared";
import { Download, Upload, FileSpreadsheet } from "lucide-react";

export function BulkUpload({ headers, sampleRow, endpoint, onDone }) {
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const downloadSample = () => {
    const ws = XLSX.utils.aoa_to_sheet([headers, sampleRow]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Template");
    XLSX.writeFile(wb, "sample-template.xlsx");
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const wb = XLSX.read(ev.target.result, { type: "array" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json(ws, { defval: "" });
        setRows(json);
        setResult(null);
        toast.success(`${json.length} rows parsed`);
      } catch {
        toast.error("Could not read file. Use the sample template (.xlsx or .csv).");
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const upload = async () => {
    if (!rows.length) { toast.error("Choose a filled file first"); return; }
    setBusy(true);
    try {
      const res = await api.post(endpoint, { rows });
      setResult(res.data);
      toast.success("Upload complete");
      onDone && onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-100 rounded-md p-3 text-sm text-slate-700">
        <p className="font-medium mb-1">How it works</p>
        <ol className="list-decimal ml-4 space-y-0.5 text-xs text-slate-600">
          <li>Download the sample Excel template.</li>
          <li>Fill your existing stock rows (keep the header row unchanged).</li>
          <li>Upload the file — stock is added and a ledger entry is recorded for each row.</li>
        </ol>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="secondary" onClick={downloadSample} data-testid="bulk-download-sample"><Download size={15} /> Download Sample Excel</Button>
        <label className="inline-flex items-center gap-2 rounded-md bg-white border border-slate-300 px-3.5 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 cursor-pointer">
          <FileSpreadsheet size={15} /> Choose File
          <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={onFile} data-testid="bulk-file-input" />
        </label>
        {fileName && <span className="text-xs text-slate-500">{fileName} · {rows.length} rows</span>}
      </div>
      {rows.length > 0 && (
        <div className="border rounded-lg overflow-x-auto max-h-56">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 sticky top-0"><tr>{headers.map((h) => <th key={h} className="px-2 py-1.5 text-left text-slate-500 font-semibold whitespace-nowrap">{h}</th>)}</tr></thead>
            <tbody>{rows.slice(0, 20).map((r, i) => (
              <tr key={i} className="border-t">{headers.map((h) => <td key={h} className="px-2 py-1 whitespace-nowrap">{String(r[h] ?? "")}</td>)}</tr>
            ))}</tbody>
          </table>
        </div>
      )}
      {result && (
        <div className="text-sm bg-emerald-50 border border-emerald-200 rounded-md p-3 text-emerald-800">
          Created: <b>{result.created}</b>{result.updated != null && <> · Updated: <b>{result.updated}</b></>}{result.skipped != null && <> · Skipped (duplicate): <b>{result.skipped}</b></>}
          {result.errors?.length > 0 && <div className="text-red-700 mt-1">{result.errors.slice(0, 5).map((e, i) => <div key={i}>{e}</div>)}</div>}
        </div>
      )}
      <div className="flex justify-end">
        <Button onClick={upload} disabled={busy || !rows.length} data-testid="bulk-upload-btn"><Upload size={15} /> {busy ? "Uploading…" : "Upload Stock"}</Button>
      </div>
    </div>
  );
}
